import { useState, useCallback, useEffect } from "react";
import { useWorkPanel } from "../../hooks/useWorkPanel";
import { useActivityPanel } from "../../hooks/useActivityPanel";
import WorkModal from "../work-modal/WorkModal";
import ActivityPanel from "../activity-panel/ActivityPanel";

const MINIMIZED_WIDTH = 280;
const PANEL_GAP = 8;
const BASE_OFFSET = 24;

import { ActivityPanelMode } from "../../store/slices/activityPanelSlice";

type PanelItem =
  | { type: "work"; id: number; minimized: boolean }
  | { type: "activity"; id: number; minimized: boolean; initialMode?: ActivityPanelMode };

const PanelBar = () => {
  const {
    panels: workPanels,
    focusedWorkId,
    closeWork,
    toggleWork,
    focusWork,
  } = useWorkPanel();

  const {
    panels: activityPanels,
    focusedActivityId,
    closeActivity,
    toggleActivity,
    focusActivity,
  } = useActivityPanel();

  // Track the last focused panel across both types
  const [lastFocusedKey, setLastFocusedKey] = useState<string | null>(null);

  // Sync lastFocusedKey when panels are opened/focused programmatically
  useEffect(() => {
    if (focusedWorkId !== null) {
      setLastFocusedKey(`work-${focusedWorkId}`);
    }
  }, [focusedWorkId]);

  useEffect(() => {
    if (focusedActivityId !== null) {
      setLastFocusedKey(`activity-${focusedActivityId}`);
    }
  }, [focusedActivityId]);

  const allPanels: PanelItem[] = [
    ...workPanels.map((p) => ({ type: "work" as const, id: p.workId, minimized: p.minimized })),
    ...activityPanels.map((p) => ({ type: "activity" as const, id: p.activityId, minimized: p.minimized, initialMode: p.initialMode })),
  ];

  const handleWorkFocus = useCallback(
    (workId: number) => {
      focusWork(workId);
      setLastFocusedKey(`work-${workId}`);
    },
    [focusWork]
  );

  const handleActivityFocus = useCallback(
    (activityId: number) => {
      focusActivity(activityId);
      setLastFocusedKey(`activity-${activityId}`);
    },
    [focusActivity]
  );

  if (allPanels.length === 0) return null;

  return (
    <>
      {allPanels.map((panel, index) => {
        const panelOffset = BASE_OFFSET + index * (MINIMIZED_WIDTH + PANEL_GAP);
        const panelKey = `${panel.type}-${panel.id}`;
        const isLastFocused = panelKey === lastFocusedKey;

        if (panel.type === "work") {
          return (
            <WorkModal
              key={panelKey}
              workId={panel.id}
              minimized={panel.minimized}
              isFocused={isLastFocused || (lastFocusedKey === null && panel.id === focusedWorkId)}
              onClose={() => closeWork(panel.id)}
              onToggleMinimize={() => toggleWork(panel.id)}
              onFocus={() => handleWorkFocus(panel.id)}
              panelOffset={panelOffset}
            />
          );
        }

        return (
          <ActivityPanel
            key={panelKey}
            activityId={panel.id}
            minimized={panel.minimized}
            isFocused={isLastFocused || (lastFocusedKey === null && panel.id === focusedActivityId)}
            initialMode={panel.initialMode}
            onClose={() => closeActivity(panel.id)}
            onToggleMinimize={() => toggleActivity(panel.id)}
            onFocus={() => handleActivityFocus(panel.id)}
            panelOffset={panelOffset}
          />
        );
      })}
    </>
  );
};

export default PanelBar;
